import React, { useEffect, useState, useContext, useRef } from 'react';
import { format } from 'date-fns';
import { requestTradingData, closeMargin, cancelMargin } from 'api/socketService';
import SocketContext, { useSocket } from 'utils/hooks/useSocket';
import { useWalletData } from 'context/WalletDataContext';
import SetTPSL from 'components/ui/SetTPSL';
import { useTradeUpdate } from 'context/TradeUpdateContext';
import { useTournament } from 'context/TournamentContext';
import { deriveTournamentPhase } from 'utils/tournamentPhase';
import { CASH_TICKER, baseOf } from 'utils/cash';
import {
  Wrap,
  TabStrip,
  TabBtn,
  ListWrap,
  EmptyState,
  LoadWrap,
  WrapTPSL,
  Card,
  CardHead,
  PairCell,
  PairIcon,
  PairName,
  SideLev,
  SideBadge,
  LevBadge,
  StatusBadge,
  CardBody,
  Row,
  L,
  V,
  CardFooter,
  TPSLBox,
  TPSLRow,
  TPSLLabel,
  TPSLValue,
  TPSLHint,
  ActionRow,
  ActionBtn,
  ConfirmRow,
  ConfirmText,
  ConfirmBtns,
  HistoryItem,
  HistoryHead,
  HistoryDate,
  HistoryBody,
} from './styled';

type Trade = {
  id: number;
  trading_pair: string;
  amount: number;
  price: number;
  market_value: number;
  type: string;
  createdAt: string;
  updatedAt: string;
  transaction_type: string;
  leverage: number;
  status: string;
  is_settled: boolean;
  used_funds: number;
  liquidation_price: number;
  description: string;
  limit?: number;
  take_profit?: number;
  stop_loss?: number;
  profit?: number;
  profit_percentage?: number;
  break_even_price?: number;
  fee?: number;
  coin_amount?: number;
};

type TabKey = 'open' | 'pending' | 'history';

const formatNum = (n: number | undefined | null, dp = 2): string => {
  if (n === undefined || n === null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
};

const TradeHistoryMarginMobile = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('open');
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [tradingData, setTradingData] = useState<Trade[]>([]);
  const { tournamentDetails, selectedCoin } = useSocket();
  const { walletData, update } = useWalletData();
  const { allCoinsValues, tradingUpdates } = useContext(SocketContext);
  const { triggerTradeUpdate, tradeUpdated } = useTradeUpdate();
  const { tournamentStatus, tournamentDetails: ctxTournamentDetails } = useTournament();
  const ctxRaw: any = ctxTournamentDetails?.data;
  const ctxAttributes = ctxRaw?.attributes ?? ctxRaw?.data ?? ctxRaw ?? {};
  const ctxEndAt: string | null | undefined = ctxAttributes?.end_at ?? ctxAttributes?.endDate;
  const phase = deriveTournamentPhase(tournamentStatus, ctxEndAt);
  const isTrading = phase === 'TRADING';
  const [isOpenTpSl, setIsOpenTpSl] = useState(false);
  const [takeProfit, setTakeProfit] = useState<any>(undefined);
  const [stopLoss, setStopLoss] = useState<any>(undefined);
  const [currentTradeId, setCurrentTradeId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isTrading) {
      setActiveTab('history');
    }
  }, [isTrading]);

  const handleAccept = () => {
    setIsOpenTpSl(false);
    setTradingData(prevData =>
      prevData.map(trade =>
        trade.id === currentTradeId
          ? { ...trade, take_profit: Number(takeProfit), stop_loss: Number(stopLoss) }
          : trade
      )
    );
  };

  const handleCancel = () => {
    setIsOpenTpSl(false);
  };

  // TEAM_004: Normalise margin history response shape from historyMargin callback
  const updateTrading = (payload: any) => {
    let trades: Trade[] | undefined;

    if (Array.isArray(payload)) {
      trades = payload as Trade[];
    } else if (Array.isArray(payload?.data)) {
      trades = payload.data as Trade[];
    } else if (Array.isArray(payload?.data?.history)) {
      trades = payload.data.history as Trade[];
    }

    if (trades && trades.length > 0) {
      setTradingData(trades);
    } else {
      console.error('Error fetching trading data:', payload);
    }
  };

  const fetchTradingData = () => {
    setLoading(true);
    const tournamentId = tournamentDetails?.data?.id;
    let tradeType = tournamentDetails?.data?.attributes?.trade_type;

    if (typeof tradeType === 'string') {
      const lower = tradeType.toLowerCase();
      if (lower === 'margin') {
        tradeType = 'Margin';
      } else if (lower === 'spot') {
        tradeType = 'Spot';
      }
    }

    let tradingPair: string | undefined;
    if (selectedCoin) {
      if (typeof selectedCoin === 'string') {
        tradingPair = selectedCoin;
      } else if (selectedCoin?.name) {
        tradingPair = selectedCoin.name as string;
      }
    }

    requestTradingData(tournamentId, tradeType, updateTrading, tradingPair);
    setLoading(false);
  };

  const didTradeUpdateMountRef = useRef(false);

  useEffect(() => {
    if (!didTradeUpdateMountRef.current) {
      didTradeUpdateMountRef.current = true;
      return;
    }

    if (tournamentDetails) {
      fetchTradingData();
    }
  }, [tradeUpdated]);

  const [currentWalletData, updateCurrentWalletData] = useState<any>(null);

  if (walletData?.length) {
    if (currentWalletData?.length) {
      walletData.forEach((element: any, i: number) => {
        if (element?.amount !== currentWalletData[i]?.amount) {
          updateCurrentWalletData(walletData);
        }
      });
    } else {
      updateCurrentWalletData(walletData);
    }
  }

  useEffect(() => {
    if (currentWalletData && selectedCoin) {
      fetchTradingData();
    }
  }, [currentWalletData, selectedCoin]);

  const filterTradesByStatus = (statuses: string[], isSettled: boolean | null = null) => {
    return tradingData.filter(trade => {
      if (isSettled !== null) {
        return statuses.includes(trade.status) && trade.is_settled === isSettled;
      }
      return statuses.includes(trade.status);
    });
  };

  const calculateProfit = (currentPrice: number, trade: Trade) => {
    const multiplier = trade.transaction_type === 'long' ? 1 : -1;
    const profit =
      (((currentPrice - trade.break_even_price) / trade.break_even_price) *
        (trade.used_funds - trade.fee) *
        trade.leverage *
        multiplier) -
      trade.fee;
    const profitPercent = (profit / trade.used_funds) * 100;
    return { profit, profitPercent };
  };

  // TEAM_004: Update tradingData whenever tradingUpdates changes (marginHistory push)
  useEffect(() => {
    if (tradingUpdates) {
      updateTrading(tradingUpdates);
      update();
    }
  }, [tradingUpdates]);

  const openTpsl = (trade: Trade) => {
    if (!isTrading) return;
    setIsOpenTpSl(true);
    setTakeProfit(trade.take_profit ? Number(trade.take_profit) : undefined);
    setStopLoss(trade.stop_loss ? Number(trade.stop_loss) : undefined);
    setCurrentTradeId(trade.id);
  };

  const performClose = (id: number) => {
    closeMargin(id, (response: any) => {
      if (response?.error) {
        console.error(response.error);
        alert(response.error);
      }
      update();
      triggerTradeUpdate();
      setConfirmingId(null);
    });
  };

  const performCancel = (id: number) => {
    cancelMargin(id, (response: any) => {
      if (response && response.error) {
        if (response.error === 'Margin not found') {
          console.warn(response.error);
          fetchTradingData();
          update();
          setConfirmingId(null);
          return;
        }
        console.error(response.error);
        alert(response.error);
        fetchTradingData();
        update();
        setConfirmingId(null);
        return;
      }
      update();
      triggerTradeUpdate();
      setConfirmingId(null);
    });
  };

  const renderTPSLBox = (trade: Trade) => {
    const tp = trade.take_profit;
    const sl = trade.stop_loss;
    return (
      <TPSLBox type="button" onClick={() => openTpsl(trade)}>
        <TPSLRow>
          <TPSLLabel $tone="bull">TP</TPSLLabel>
          <TPSLValue className={tp ? '' : 'muted'}>
            {tp ? formatNum(Number(tp)) : 'Not set'}
          </TPSLValue>
        </TPSLRow>
        <TPSLRow>
          <TPSLLabel $tone="bear">SL</TPSLLabel>
          <TPSLValue className={sl ? '' : 'muted'}>
            {sl ? formatNum(Number(sl)) : 'Not set'}
          </TPSLValue>
        </TPSLRow>
        <TPSLHint>Tap to edit</TPSLHint>
      </TPSLBox>
    );
  };

  const openTrades = filterTradesByStatus(['filled'], false);
  const pendingTrades = filterTradesByStatus(['pending'], false);
  const historyTrades = filterTradesByStatus(['filled', 'canceled']).filter(t => {
    if (t.status === 'canceled') return true;
    return t.is_settled;
  });

  return (
    <Wrap>
      <TabStrip>
        {isTrading && (
          <>
            <TabBtn
              type="button"
              className={activeTab === 'open' ? 'active' : ''}
              onClick={() => setActiveTab('open')}
            >
              Open ({openTrades.length})
            </TabBtn>
            <TabBtn
              type="button"
              className={activeTab === 'pending' ? 'active' : ''}
              onClick={() => setActiveTab('pending')}
            >
              Pending ({pendingTrades.length})
            </TabBtn>
          </>
        )}
        <TabBtn
          type="button"
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          History
        </TabBtn>
      </TabStrip>

      {isOpenTpSl && (
        <WrapTPSL>
          <SetTPSL
            takeProfit={takeProfit}
            setTakeProfit={setTakeProfit}
            stopLoss={stopLoss}
            setStopLoss={setStopLoss}
            onAccept={handleAccept}
            onCancel={handleCancel}
            tradeId={currentTradeId}
          />
        </WrapTPSL>
      )}

      {!isOpenTpSl && (
        <>
          {activeTab === 'open' && (
            <ListWrap>
              {loading ? (
                <LoadWrap />
              ) : openTrades.length === 0 ? (
                <EmptyState>No open positions</EmptyState>
              ) : (
                openTrades.map(trade => {
                  const tradingPairKey = trade.trading_pair.split('/')[0].toLowerCase();
                  const currentPrice = allCoinsValues?.[tradingPairKey]?.last ?? 0;
                  const { profit, profitPercent } = calculateProfit(currentPrice, trade);
                  const profitClass = profit >= 0 ? 'bull' : 'bear';
                  const side = trade.transaction_type === 'long' ? 'long' : 'short';
                  const baseAsset = baseOf(trade.trading_pair);
                  const isConfirming = confirmingId === trade.id;

                  return (
                    <Card key={trade.id}>
                      <CardHead>
                        <PairCell>
                          <PairIcon
                            $src={`../icons/tradingPair/use/${baseAsset}.svg`}
                          />
                          <PairName>{trade.trading_pair}</PairName>
                        </PairCell>
                        <SideLev>
                          <SideBadge $side={side}>
                            {side === 'long' ? 'Long' : 'Short'}
                          </SideBadge>
                          <LevBadge>×{trade.leverage}</LevBadge>
                        </SideLev>
                      </CardHead>

                      <CardBody>
                        <Row>
                          <L>Size</L>
                          <V>
                            {formatNum(trade.coin_amount, 4)} {baseAsset}
                          </V>
                        </Row>
                        <Row>
                          <L>Used Funds</L>
                          <V>{formatNum(trade.used_funds)} {CASH_TICKER}</V>
                        </Row>
                        <Row>
                          <L>Entry</L>
                          <V>{formatNum(trade.price)}</V>
                        </Row>
                        <Row>
                          <L>Mark</L>
                          <V>{formatNum(currentPrice)}</V>
                        </Row>
                        <Row>
                          <L>Liq.</L>
                          <V className="bear">{formatNum(trade.liquidation_price)}</V>
                        </Row>
                        <Row>
                          <L>P&amp;L</L>
                          <V className={profitClass}>
                            {formatNum(profit)} {CASH_TICKER} ({formatNum(profitPercent)}%)
                          </V>
                        </Row>
                      </CardBody>

                      <CardFooter>
                        {!isConfirming && renderTPSLBox(trade)}

                        {!isConfirming && (
                          <ActionRow>
                            <ActionBtn
                              type="button"
                              $tone="danger"
                              onClick={() => setConfirmingId(trade.id)}
                            >
                              Close position
                            </ActionBtn>
                          </ActionRow>
                        )}

                        {isConfirming && (
                          <ConfirmRow>
                            <ConfirmText>Close this position?</ConfirmText>
                            <ConfirmBtns>
                              <ActionBtn
                                type="button"
                                $tone="danger"
                                onClick={() => performClose(trade.id)}
                              >
                                Yes
                              </ActionBtn>
                              <ActionBtn
                                type="button"
                                onClick={() => setConfirmingId(null)}
                              >
                                No
                              </ActionBtn>
                            </ConfirmBtns>
                          </ConfirmRow>
                        )}
                      </CardFooter>
                    </Card>
                  );
                })
              )}
            </ListWrap>
          )}

          {activeTab === 'pending' && (
            <ListWrap>
              {pendingTrades.length === 0 ? (
                <EmptyState>No pending orders</EmptyState>
              ) : (
                pendingTrades.map(trade => {
                  const side = trade.transaction_type === 'long' ? 'long' : 'short';
                  const baseAsset = baseOf(trade.trading_pair);
                  const isConfirming = confirmingId === trade.id;

                  return (
                    <Card key={trade.id}>
                      <CardHead>
                        <PairCell>
                          <PairIcon
                            $src={`../icons/tradingPair/use/${baseAsset}.svg`}
                          />
                          <PairName>{trade.trading_pair}</PairName>
                        </PairCell>
                        <SideLev>
                          <SideBadge $side={side}>
                            {side === 'long' ? 'Long' : 'Short'}
                          </SideBadge>
                          <LevBadge>×{trade.leverage}</LevBadge>
                        </SideLev>
                      </CardHead>

                      <CardBody>
                        <Row>
                          <L>Limit</L>
                          <V>{formatNum(trade.limit)} {CASH_TICKER}</V>
                        </Row>
                        <Row>
                          <L>Used Funds</L>
                          <V>{formatNum(trade.used_funds)} {CASH_TICKER}</V>
                        </Row>
                        <Row>
                          <L>Size</L>
                          <V>
                            {formatNum(trade.coin_amount ?? trade.amount, 4)} {baseAsset}
                          </V>
                        </Row>
                      </CardBody>

                      <CardFooter>
                        {!isConfirming && renderTPSLBox(trade)}

                        {!isConfirming && (
                          <ActionRow>
                            <ActionBtn
                              type="button"
                              $tone="danger"
                              onClick={() => setConfirmingId(trade.id)}
                            >
                              Cancel order
                            </ActionBtn>
                          </ActionRow>
                        )}

                        {isConfirming && (
                          <ConfirmRow>
                            <ConfirmText>Cancel this order?</ConfirmText>
                            <ConfirmBtns>
                              <ActionBtn
                                type="button"
                                $tone="danger"
                                onClick={() => performCancel(trade.id)}
                              >
                                Yes
                              </ActionBtn>
                              <ActionBtn
                                type="button"
                                onClick={() => setConfirmingId(null)}
                              >
                                No
                              </ActionBtn>
                            </ConfirmBtns>
                          </ConfirmRow>
                        )}
                      </CardFooter>
                    </Card>
                  );
                })
              )}
            </ListWrap>
          )}

          {activeTab === 'history' && (
            <ListWrap>
              {historyTrades.length === 0 ? (
                <EmptyState>No history yet</EmptyState>
              ) : (
                historyTrades.map(trade => {
                  const side = trade.transaction_type === 'long' ? 'long' : 'short';
                  const baseAsset = baseOf(trade.trading_pair);
                  const isFilled = trade.status === 'filled';
                  const profitVal = typeof trade.profit === 'number' ? trade.profit : null;

                  return (
                    <HistoryItem key={trade.id}>
                      <HistoryHead>
                        <PairCell>
                          <PairIcon
                            $src={`../icons/tradingPair/use/${baseAsset}.svg`}
                          />
                          <PairName>{trade.trading_pair}</PairName>
                        </PairCell>
                        <SideLev>
                          <SideBadge $side={side}>
                            {side === 'long' ? 'Long' : 'Short'}
                          </SideBadge>
                          <LevBadge>×{trade.leverage}</LevBadge>
                          <StatusBadge $tone={isFilled ? 'bull' : 'muted'}>
                            {isFilled ? 'Filled' : 'Canceled'}
                          </StatusBadge>
                        </SideLev>
                      </HistoryHead>

                      <HistoryBody>
                        <Row>
                          <L>Price</L>
                          <V>{formatNum(trade.price)} {CASH_TICKER}</V>
                        </Row>
                        <Row>
                          <L>Size</L>
                          <V>
                            {formatNum(trade.coin_amount, 4)} {baseAsset}
                          </V>
                        </Row>
                        <Row>
                          <L>Used Funds</L>
                          <V>{formatNum(trade.used_funds)} {CASH_TICKER}</V>
                        </Row>
                        {isFilled && trade.is_settled && profitVal !== null && (
                          <Row>
                            <L>Profit</L>
                            <V className={profitVal >= 0 ? 'bull' : 'bear'}>
                              {formatNum(profitVal)} {CASH_TICKER}
                            </V>
                          </Row>
                        )}
                        <Row>
                          <L>Date</L>
                          <V className="muted">
                            {format(new Date(trade.createdAt), 'dd MMM HH:mm')}
                          </V>
                        </Row>
                      </HistoryBody>
                    </HistoryItem>
                  );
                })
              )}
            </ListWrap>
          )}
        </>
      )}
    </Wrap>
  );
};

export default TradeHistoryMarginMobile;
